import type {
  MatchingOffers,
  MatchOptions,
  MatchTranche,
  PublicMatchingOffers,
  Zone
} from 'types/match'

import { useEffect, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import { URL_PARAM } from 'common'
import copy from 'copy.json'
import { ArrowLeftIcon, ClockIcon, HeartPlusIcon, InfoIcon } from 'icons'
import { useStore } from 'store'
import { dimmBackground } from 'utils'

import Button from 'components/Button'
import ContentHeader from 'components/ContentHeader'
import CopyButton from 'components/CopyButton'
import DeleteButton from 'components/DeleteButton'
import MonthlyDataGraph from 'components/MonthlyDataGraph'
import PageContainer from 'components/PageContainer'
import Spinner from 'components/Spinner'
import { parseMatchOptions } from '../../utils/tranche-utils'

import { MatchOptionsEditor } from 'components/OfferOptionsEditor'
import RecalculateButton from 'components/RecalculateButton'
import ConfirmationWarnModal from 'modals/ConfirmationWarnModal'
import ConsumptionDetailsModal from 'modals/ConsumptionDetailsModal'
import DeleteResourceModal from 'modals/DeleteResourceModal'
import { adminPanelPaths, basePaths } from 'paths'
import { MatchOffersTable } from './MatchOffersTable'
import OfferStatus from './OfferStatus'
import YearSummary from './YearSummary'
import ZonesModal from './ZonesModal'

const texts = copy.page.matches
const button = copy.common.button

type Props = {
  useMatchId?: boolean
  useDiffId?: boolean
}

export default function SearchMatchingOffers({ useMatchId, useDiffId }: Props) {
  const { id } = useParams()
  const { api } = useStore()
  const location = useLocation()
  const navigate = useNavigate()
  const [data, setData] = useState<MatchingOffers | PublicMatchingOffers | null>(null)
  const [loading, setLoading] = useState(true)
  const [refetching, setRefetching] = useState(false)
  const [rdnMethodAvailable, setRdnMethodAvailable] = useState(false)
  const [isZonesModalOpen, setIsZonesModalOpen] = useState(false)
  const [zones, setZones] = useState<Zone[]>([])
  const [deleteMatchModalOpen, setDeleteMatchModalOpen] = useState(false)
  const [deleteMatchReasonModalOpen, setDeleteMatchReasonModalOpen] = useState(false)
  const [isInterestReported, setIsInterestReported] = useState(false)
  const [consumptionDetailsModalOpen, setConsumptionDetailsModalOpen] = useState(false)
  const [matchOptions, setMatchOptions] = useState<MatchOptions & MatchTranche>({
    rdn_pricing_method: {
      kind: 'CALENDAR_MOVING_AVERAGE',
      window_days: 1,
      history_years: 3
    },
    rdn_energy_pricing_extra_percent: 0,
    tranche_energy_pricing_extra_percent: 0,
    cost_guarantee_of_source: 0,
    cost_excise: 0,
    cost_rdn_handling: 0,
    cost_ppa_handling: 0,
    cost_tranche_handling: 0,
    custom_offers_data: []
  })

  const isBackPageSaved = location?.state?.rememberBackPage

  const matchingOffersData = data as MatchingOffers
  const linkToDemand = `${adminPanelPaths.buyer.search}/${matchingOffersData?.demand_data?.buyer_id}?${URL_PARAM.electricEnergyMeterNumber}=${matchingOffersData?.demand_data?.electric_energy_meter_number}`

  const openZonesModal = (zones: Zone[]) => {
    setZones(Object.values(zones))
    setIsZonesModalOpen(true)
  }

  useEffect(() => {
    if (!id) return

    const fetchData = async () => {
      if (useMatchId) {
        const newData = await api.getMatches(Number(id))
        setData(newData)
        setMatchOptions(parseMatchOptions(newData))
      }
      if (useDiffId) setData(await api.getPublicMatches(id))
      setLoading(false)
    }

    fetchData()
  }, [api, id, useDiffId, useMatchId])

  useEffect(dimmBackground)

  const handleMatchDelete = async (matchId: number, reason: string) => {
    const success = await api.deleteMatch(matchId, reason)

    if (!success) return false

    refetchData()

    return true
  }

  const handleMatchRecalculate = async () => {
    if (useMatchId) {
      setRefetching(true)
      await api.recalculateMatch(Number(id), matchOptions)
      refetchData()
    }
  }

  const refetchData = async () => {
    if (!data || !('match_id' in data) || data.match_id === undefined) return

    setRefetching(true)
    setData(await api.getMatches(data.match_id))
    setRefetching(false)
  }

  const handleDownload = () => {
    if (useMatchId && data?.match_id) {
      api.getIndividualEnergyPricingFile(data.match_id.toString())
    }

    if (useDiffId && data?.diff_id) {
      api.getIndividualEnergyPricingFile(data.diff_id, true)
    }
  }

  const handleReportInterest = async () => {
    if (useDiffId && data?.diff_id) {
      setIsInterestReported(await api.reportInterest(data.diff_id))
    }
  }

  const hasRightStatus = data?.status && ['ACTIVE', 'BLOCKED'].includes(data.status)
  const showInterestReportedInfo = (isInterestReported || data?.interest_reported) && hasRightStatus

  if (!id) return <Navigate to={basePaths.admin_panel} />
  if (loading) return <Spinner size={60} centered style={{ color: 'var(--white)' }} />
  if (!data) return null

  return (
    <>
      <Content>
        {refetching && (
          <ContentLoading>
            <Spinner size={60} centered style={{ color: 'var(--white)' }} />
          </ContentLoading>
        )}
        <ContentHeader>
          {useMatchId && (
            <BackArrowButton
              onClick={() =>
                isBackPageSaved ? navigate(-1) : navigate(adminPanelPaths.matches_search)
              }
            >
              <ArrowLeftIcon />
            </BackArrowButton>
          )}

          <h1>{useDiffId ? `${texts.heading_2}: ${data.diff_id}` : texts.heading}</h1>

          {useMatchId && (
            <DemandEnergyMeterNumber>
              {texts.energy_meter_number_demand}:{' '}
              <Link to={linkToDemand}>
                {matchingOffersData.demand_data.electric_energy_meter_number}
              </Link>
            </DemandEnergyMeterNumber>
          )}

          <ButtonWrapper>
            {useDiffId && showInterestReportedInfo && (
              <InterestReportedInfo>
                <InfoIcon stroke="var(--green)" />
                <div>
                  <strong>{texts.interest_reported.header}</strong>
                  <p>{texts.interest_reported.paragraph}</p>
                </div>
              </InterestReportedInfo>
            )}

            {useDiffId && !showInterestReportedInfo && (
              <Button onClick={handleReportInterest}>
                <HeartPlusIcon /> {texts.button.report_interest}
              </Button>
            )}

            <Button secondary onClick={() => setConsumptionDetailsModalOpen(true)}>
              <ClockIcon /> {texts.consumption_button}
            </Button>
          </ButtonWrapper>
        </ContentHeader>

        <Info>
          <OfferInfoWrapper>
            <OfferStatus
              currentStatus={data.status}
              statusHistory={'status_history' in data ? data.status_history : []}
              reservationsHistory={'reservations_history' in data ? data.reservations_history : []}
              currentReservationLength={
                'current_reservation_length' in data ? data.current_reservation_length : 0
              }
              statusOnly={useDiffId}
              loading={false}
              onStatusChange={refetchData}
            />

            <MatchOffersTable
              data={data}
              useMatchId={useMatchId ?? false}
              offerOptions={matchOptions}
            />
          </OfferInfoWrapper>

          <YearSummary
            data={data.yearly_data}
            onShowZones={openZonesModal}
            downloadIndividualEnergyPricingFile={handleDownload}
            loadRdnPricingDetails={() => api.getRdnPricingDetails(
              useDiffId ? data.diff_id : String(data.match_id), !!useDiffId
            )}
            loadImbalanceDetails={() => api.getImbalanceDetails(
              useDiffId ? data.diff_id : String(data.match_id), !!useDiffId
            )}
            style={{ minWidth: '50%' }}
          />
        </Info>

        <MonthlyDataGraph data={data.monthly_data} onShowZones={openZonesModal} />
      </Content>

      {useMatchId && (
        <Content>
          <Footer>
            <UUID>
              <span>{texts.uuid}:</span>
              <div>
                <span>{data.diff_id}</span>
                <CopyButton value={`${window.location.origin}/offered-matches/${data.diff_id}`}>
                  {texts.button.copy_link}
                </CopyButton>
              </div>
            </UUID>

            {data.status !== 'FINALIZED' && data.status !== 'DELETED' && (
              <ButtonWrapper>
                <RecalculateButton
                  onClick={handleMatchRecalculate}
                  disabled={!rdnMethodAvailable}
                  title={!rdnMethodAvailable
                    ? 'Nie potwierdzono dostępności wybranej metody RDN'
                    : undefined}
                />
                <DeleteButton onClick={() => setDeleteMatchModalOpen(true)} />
              </ButtonWrapper>
            )}

            <MatchOptionsEditor
              data={data}
              matchId={Number(id)}
              value={matchOptions}
              onValueChange={setMatchOptions}
              refetchData={refetchData}
              onRdnMethodAvailabilityChange={setRdnMethodAvailable}
              disabled={data.status === 'FINALIZED' || data.status === 'DELETED'}
            />
          </Footer>
        </Content>
      )}

      <ZonesModal
        isOpen={isZonesModalOpen}
        zones={zones}
        onClose={() => setIsZonesModalOpen(false)}
      />

      <ConfirmationWarnModal
        title={texts.modal.delete.title}
        description={texts.modal.delete.description}
        isModalOpen={deleteMatchModalOpen}
        onModalClose={() => setDeleteMatchModalOpen(false)}
        onModalConfirmation={() => {
          setDeleteMatchReasonModalOpen(true)
          setDeleteMatchModalOpen(false)
        }}
        confirmButtonLabel={button.delete}
        closeButtonLabel={button.close}
      />

      <DeleteResourceModal
        title={texts.modal.delete_reason.title}
        status={data?.status}
        resourceId={Number(id)}
        isModalOpen={deleteMatchReasonModalOpen}
        onModalClose={() => setDeleteMatchReasonModalOpen(false)}
        onModalConfirmation={(reason: string) => handleMatchDelete(Number(id), reason)}
        confirmButtonLabel={button.delete}
        closeButtonLabel={button.cancel}
      />

      <ConsumptionDetailsModal
        demandId={useDiffId ? data.diff_id : `${(data as MatchingOffers).demand_data.demand_id}`}
        isModalOpen={consumptionDetailsModalOpen}
        onModalClose={() => setConsumptionDetailsModalOpen(false)}
      />
    </>
  )
}

const Content = styled(PageContainer)`
  gap: 24px;
  position: relative;

  h3 {
    font: var(--600-16);
  }

  a {
    color: inherit;
  }
`

const ContentLoading = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  pointer-events: none;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Info = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 24px;

  > div:first-child {
    display: flex;
    flex-direction: column;
    gap: 32px;
  }
`

const OfferInfoWrapper = styled.div`
  max-width: 100%;
  flex: 1;
`

const Footer = styled.footer`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
`

const UUID = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  > span {
    color: var(--gray);
    font: var(--600-12);
  }

  > div {
    display: flex;
    align-items: center;
    gap: 8px;

    > span {
      font: var(--400-13);
    }
  }
`

const BackArrowButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
`

const InterestReportedInfo = styled.div`
  display: flex;
  flex-direction: row;
  margin-left: auto;
  gap: 8px;

  font: var(--400-13);

  strong {
    color: var(--green);
  }
`

const DemandEnergyMeterNumber = styled.div`
  font: var(--400-15);

  a {
    color: var(--green);
    text-decoration: none;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`
